// Third party
import React from 'react';
import classNames from 'classnames';
import get from 'lodash/get';

// Components
import ESGMetricsDisclaimer from '../disclaimers/comparison/ESGMetricsDisclaimer';

// Utils
import { formatMessage, formatOrdinalSuffix, FormattedMessage } from '@/utils/i18n';
import { fundToComparisonItem } from '@/utils/comparison';
import { RankingLimit, CarbonIntensityLimit, ImpliedTemperatureLimit, ESGRatingClassification, ESGColourDefinition, TEMPERATURE_RANKING } from '@/enums/Esg';
import Fund from '@/types/fund';
import AppRegion from '@/types/app-region';

type Props = {
  fund: Fund;
  appRegion: AppRegion;
};

type Field = {
  i18nKey: string;
  key: string;
  classes?: string;
  getFieldValue: (value: number | string | any) => any;
  getFieldColor: (value: number | string | any) => any;
  getExtraInfo?: (value: number) => string;
};

export default function FundDetailsESGMetrics({ fund, appRegion }: Props) {
  const getESGMetricsFields = () => [
    {
      i18nKey: 'ESGComparisonTool.Rating.MetricsTitle',
      key: 'esg.rating',
      getFieldValue: (fieldValue: any) => fieldValue?.value,
      getFieldColor: (value: { classification: string }) => {
        switch (value.classification) {
          case ESGRatingClassification.LEADER:
            return ESGColourDefinition.TEXT_ALIGNED;
          case ESGRatingClassification.AVERAGE:
            return ESGColourDefinition.TEXT_MODERATE;
          case ESGRatingClassification.LAGGARD:
            return ESGColourDefinition.TEXT_STRONGLY_MISALIGNED;
          default:
            return ESGColourDefinition.NA;
        }
      },
    },
    {
      i18nKey: 'ESGComparisonTool.Ranking.PeerRank',
      key: 'esg.peerRank',
      getFieldValue: (value: number) => value?.toFixed(0),
      getFieldColor: (value: number | string) => {
        if (value > RankingLimit.HIGH) return ESGColourDefinition.TEXT_ALIGNED;
        if (value >= RankingLimit.MODERATE) return ESGColourDefinition.TEXT_MODERATE;
        return ESGColourDefinition.TEXT_STRONGLY_MISALIGNED;
      },
      getExtraInfo: (value: number) => `${formatOrdinalSuffix(value)}/100`,
    },
    {
      i18nKey: 'ESGComparisonTool.Ranking.GlobalRank',
      key: 'esg.globalRank',
      getFieldValue: (value: number) => value?.toFixed(0),
      getFieldColor: (value: number | string) => {
        if (value > RankingLimit.HIGH) return ESGColourDefinition.TEXT_ALIGNED;
        if (value >= RankingLimit.MODERATE) return ESGColourDefinition.TEXT_MODERATE;
        return ESGColourDefinition.TEXT_STRONGLY_MISALIGNED;
      },
      getExtraInfo: (value: number) => `${formatOrdinalSuffix(value)}/100`,
    },
    {
      i18nKey: 'ESGComparisonTool.Carbon.Title',
      key: 'esg.carbonIntensity',
      classes: 'h-margin',
      getFieldValue: (value: number) => value?.toFixed(1),
      getFieldColor: (value: number | string) => {
        if (value < CarbonIntensityLimit.LOW) return ESGColourDefinition.TEXT_ALIGNED;
        if (value < CarbonIntensityLimit.MODERATE) return ESGColourDefinition.TEXT_MODERATE;
        return ESGColourDefinition.TEXT_STRONGLY_MISALIGNED;
      },
      getExtraInfo: () => formatMessage('ESGComparisonTool.Carbon.TitleSubText'),
    },
    {
      i18nKey: 'ESGComparisonTool.ImpliedTemperatureRise.Title',
      key: 'esg.impliedTemperatureRise',
      getFieldValue: (value: number) => value?.toFixed(1),
      getFieldColor: (value: number | string) => {
        if (value <= ImpliedTemperatureLimit.ALIGNED_LOW) return ESGColourDefinition.TEXT_STRONGLY_ALIGNED;
        if (value <= ImpliedTemperatureLimit.ALIGNED) return ESGColourDefinition.TEXT_ALIGNED;
        if (value <= ImpliedTemperatureLimit.MISALIGNED) return ESGColourDefinition.TEXT_MODERATE;
        return ESGColourDefinition.TEXT_STRONGLY_MISALIGNED;
      },
      getExtraInfo: (value: number) => {
        let temperatureRanking = TEMPERATURE_RANKING.STRONGLY_MISALIGNED;
        if (!value) temperatureRanking = TEMPERATURE_RANKING.UNAVAILABLE;
        if (value <= ImpliedTemperatureLimit.MISALIGNED) temperatureRanking = TEMPERATURE_RANKING.MISALIGNED;
        if (value <= ImpliedTemperatureLimit.ALIGNED) temperatureRanking = TEMPERATURE_RANKING.ALIGNED;
        return `°C (${formatMessage(`ESGComparisonTool.ImpliedTemperatureRise.TemperatureRanking.${temperatureRanking}`)})`;
      },
    },
  ];

  const generateESGField = (fund: Fund, field: Field) => {
    const esgValue = get(fund, field.key);

    if (!esgValue || (field.key === 'esg.rating' && !esgValue.value)) {
      return (
        <span className="stat-value" style={{ '--value-color': ESGColourDefinition.NA } as React.CSSProperties}>
          {formatMessage('Common.NotApplicable')}
        </span>
      );
    }

    const fieldValue = field.getFieldValue(esgValue) || '';
    const fieldColor = field.getFieldColor(esgValue) || ESGColourDefinition.NA;
    const extraInfo = (field.getExtraInfo && field.getExtraInfo(esgValue)) || '';

    return (
      <span className="stat-value" style={{ '--value-color': fieldColor } as React.CSSProperties}>
        <span>{fieldValue}</span>
        <span className={classNames(field.classes)}>{extraInfo}</span>
      </span>
    );
  };

  return (
    <div className="fund-details-esg-metrics fund-stats">
      <div className="group-header d-flex">
        <FormattedMessage tagName="h5" id="ESGComparisonTool.MetricsTitle" />
      </div>
      <div>
        {getESGMetricsFields().map(field => (
          <div key={field.key} className="stat">
            <FormattedMessage tagName="span" id={field.i18nKey} />
            {generateESGField(fund, field)}
          </div>
        ))}
        <div className="stats-disclaimer">
          <ESGMetricsDisclaimer showItemNames={false} items={[fundToComparisonItem(fund, appRegion)]} />
        </div>
      </div>
    </div>
  );
}
